// Attachments — a shared, reusable card: drag-drop + "choose files" upload, a list (filename ·
// size · uploaded-by/at · download · delete), mounted on both a document detail page and a
// contact detail page (documents-parity sweep Wave C1, owner 2026-09-03: "drag-drop files onto a
// document + a contact, with list + download + delete"). Upload goes through the JSON base64 path
// ({filename, content_base64, mime}) — api.js's api.post already carries the bearer + 401-refresh
// for us, so there is no need for multipart on the client. Download uses api.download() (see
// shared/api.js — it fetches with the auth header then triggers a Blob download; a plain <a href>
// can't carry a bearer token). Delete is permanent, so it goes through shared/confirm.js's
// rowDelete (never window.confirm, per CLAUDE.md).
//
// tina4 gotcha (CLAUDE.md — validated, do not relearn the hard way): this card is always MOUNTED
// INSIDE an already-reactive parent view (document_detail.js's body(), contacts.js's
// contactDetailScreen body() both re-render on lots of unrelated signals). So attachmentsCard(...)
// is a FACTORY called ONCE per screen instance (like `const doc = signal(null)` at the top of
// documentDetailScreen) — it owns its signals and kicks off the first load immediately — and it
// returns a plain view FUNCTION that the caller invokes PLAINLY (e.g. `${attachCard()}`, exactly
// like paper(d)/historyBlock()/balanceCard(d) in document_detail.js), never wrapped in a nested
// ${()=>…}. The view reads every signal it needs at its own top and interpolates plain values only
// — no nested reactive slots inside its returned template — because a reactive slot created while
// an OUTER reactive slot is re-running does not get cleaned up right and duplicates on re-render.
// The drag-over highlight is deliberately plain DOM classList (not a signal), so a drag gesture
// never forces the whole parent page to re-render mid-drag.
import { html, signal } from './t4.js';
import { api } from './api.js';
import { notice } from './notice.js';
import { rowDelete } from './confirm.js';
import { t } from './i18n.js';

let uid = 0;

function fmtBytes(n){
  const v = Number(n) || 0;
  if(v < 1024) return v + ' B';
  if(v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB';
  return (v / (1024 * 1024)).toFixed(1) + ' MB';
}

// File → base64 (no data: prefix) via FileReader — real binary-safe read, no multipart needed.
function readAsBase64(file){
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error || new Error('Could not read the file'));
    fr.onload = () => {
      const s = String(fr.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    fr.readAsDataURL(file);
  });
}

/**
 * attachmentsCard({ base, attachmentBase, title }) → a view function.
 *   base            list/upload root, e.g. '/companies/42/documents/7' or '/companies/42/contacts/9'
 *                   — list is GET base+'/attachments', upload is POST base+'/attachments'.
 *   attachmentBase  per-attachment root, e.g. '/companies/42/attachments' (documents) or
 *                   '/companies/42/contact-attachments' (contacts) — download is
 *                   GET attachmentBase+'/'+id, delete is POST attachmentBase+'/'+id+'/delete'.
 *   title           card heading, e.g. t('doc.attachments', 'Attachments').
 * Call ONCE per screen instance; call the RETURNED function plainly wherever the card belongs.
 */
export function attachmentsCard({ base, attachmentBase, title }){
  const list = signal([]), loading = signal(true), loadErr = signal('');
  const uploading = signal(false);
  const confirmId = signal(null), deleting = signal(false), delErr = signal('');
  const inputId = 'attach-file-' + (++uid);
  let seq = 0;

  async function load(){
    const mySeq = ++seq;
    loading.value = true; loadErr.value = '';
    try {
      const r = await api.get(base + '/attachments');
      if(mySeq !== seq) return;             // a newer load already superseded this one
      list.value = r.data || [];
    } catch(e){
      if(mySeq !== seq) return;
      loadErr.value = (e.data && e.data.error) || e.message || t('attach.load_failed', 'Could not load attachments');
    }
    if(mySeq === seq) loading.value = false;
  }
  load();

  async function uploadOne(file){
    const content_base64 = await readAsBase64(file);
    return api.post(base + '/attachments', { filename: file.name, content_base64, mime: file.type || undefined });
  }

  async function uploadFiles(files){
    const chosen = Array.from(files || []).filter(Boolean);
    if(!chosen.length) return;
    uploading.value = true;
    let ok = 0;
    for(const file of chosen){
      try { await uploadOne(file); ok++; }
      catch(e){
        const msg = (e.data && e.data.error) || e.message || t('attach.upload_failed', 'Could not upload');
        notice(file.name + ': ' + msg, 'bad');
      }
    }
    uploading.value = false;
    if(ok === 1) notice(t('attach.uploaded_one', '1 file attached'), 'ok');
    else if(ok > 1) notice(ok + ' ' + t('attach.uploaded_many', 'files attached'), 'ok');
    if(ok) await load();
  }

  // native file picker via a real <button> (keyboard/AT friendly) driving a visually-hidden input
  function chooseClick(){
    const el = document.getElementById(inputId);
    if(el) el.click();
  }
  function onPick(e){
    // Snapshot the chosen files into a plain array BEFORE clearing the input. `e.target.files` is a
    // LIVE FileList that the browser empties IN PLACE the instant `e.target.value = ''` runs, so the
    // old order (capture the FileList, reset, then read it in uploadFiles) handed uploadFiles a
    // zero-length list and nothing was ever attached (the picker path uploaded nothing on every
    // surface — editor, document detail, contact detail; only drag-drop, which reads dataTransfer,
    // worked). Array.from freezes the entries so the reset can't drain them.
    const files = Array.from(e.target.files || []);
    e.target.value = '';                    // let the SAME filename be re-chosen later
    uploadFiles(files);
  }
  // drag-over state is plain DOM (not a signal) — a dragenter/dragleave storm must never force the
  // whole parent screen to re-render mid-drag (see the module note above)
  function onDragOver(e){ e.preventDefault(); e.currentTarget.classList.add('is-dragover'); }
  function onDragLeave(e){ e.currentTarget.classList.remove('is-dragover'); }
  function onDrop(e){
    e.preventDefault();
    e.currentTarget.classList.remove('is-dragover');
    const dt = e.dataTransfer;
    if(dt && dt.files && dt.files.length) uploadFiles(dt.files);
  }

  function armDelete(id){ delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try {
      await api.post(attachmentBase + '/' + id + '/delete', {});
      confirmId.value = null;
      await load();
      notice(t('attach.deleted', 'Attachment deleted'), 'ok');
    } catch(e){
      delErr.value = (e.data && e.data.error) || e.message || t('attach.delete_failed', 'Could not delete');
    }
    deleting.value = false;
  }

  function downloadOne(id, filename){
    api.download(attachmentBase + '/' + id, filename)
      .catch(e => notice((e.data && e.data.error) || e.message || t('attach.download_failed', 'Could not download'), 'bad'));
  }

  // ── the view — call PLAINLY inside the caller's own reactive body. Every signal this needs is
  // read HERE, at the top; everything below is a plain value (no nested ${()=>…}). ──────────────
  return function attachmentsView(){
    const items = list.value, isLoading = loading.value, lErr = loadErr.value, isUploading = uploading.value;
    const cId = confirmId.value, isDeleting = deleting.value, dErr = delErr.value;
    return html`<div class="card attach-card">
      <div class="card-body">
        <div class="rail-card-title">${title}</div>
        <div class="attach-drop" @dragover=${onDragOver} @dragleave=${onDragLeave} @drop=${onDrop}>
          <span class="attach-drop-hint">${t('attach.drop_hint', 'Drag files here, or')}</span>
          <button type="button" class="btn btn-sm btn-outline" ?disabled=${isUploading} @click=${chooseClick}>${isUploading ? t('attach.uploading', 'Uploading…') : t('attach.choose', 'Choose files')}</button>
          <input id=${inputId} class="attach-file-input" type="file" multiple ?disabled=${isUploading}
            aria-label=${t('attach.choose', 'Choose files')} placeholder=${t('attach.choose_ph', 'e.g. invoice-scan.pdf')}
            @change=${onPick}>
        </div>
        ${isLoading ? html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`
          : lErr ? html`<div class="row-err">${lErr}</div>`
          : items.length ? html`<div class="row-list attach-list">${items.map(a => html`
              <div class="row-card attach-row">
                <span class="row-main"><b>${a.filename}</b>
                  <span class="row-sub">${fmtBytes(a.size_bytes)}${a.uploaded_by_email ? ' · ' + a.uploaded_by_email : ''}${a.uploaded_at ? ' · ' + String(a.uploaded_at).slice(0, 10) : ''}</span></span>
                <span class="row-actions">
                  <button type="button" class="btn btn-sm btn-outline" @click=${() => downloadOne(a.id, a.filename)}>${t('attach.download', 'Download')}</button>
                  ${rowDelete({ armed: cId === a.id, busy: isDeleting, error: cId === a.id ? dErr : '',
                    onArm: () => armDelete(a.id), onCancel: cancelDelete, onDelete: () => doDelete(a.id) })}
                </span>
              </div>`)}</div>`
          : html`<div class="muted-block attach-empty">${t('attach.none', 'No attachments yet.')}</div>`}
      </div>
    </div>`;
  };
}
